import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fetchPosts, submitBooking, fetchMyBookings, updateBooking, cancelBooking } from "../services/homeApi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from "../components/Home/Footer";
import "./KonsultasjonDetalj.css";

function KonsultasjonDetalj() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, email, name, sex, age, role } = useAuth();
  const isMember = role === "member";

  const [post, setPost] = useState(null);
  const [existingBooking, setExistingBooking] = useState(null);
  const [editing, setEditing] = useState(false);
  const [newLevel, setNewLevel] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts()
      .then((posts) => {
        setPost(posts.find((p) => String(p.id) === String(id)) || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (user && post) {
      fetchMyBookings()
        .then((bookings) => setExistingBooking(bookings.find((b) => b.post_id === post.id) || null))
        .catch(console.error);
    }
  }, [user, post]);

  const handleBooking = async (e) => {
    e.preventDefault();
    try {
      await submitBooking(post.id, {
        student_name: e.target.studentName.value,
        student_email: e.target.studentEmail.value,
        sex: e.target.sex.value,
        age_range: e.target.age.value,
        tajweed_level: e.target.level.value,
        phone: e.target.phone.value,
      });
      toast.success("Påmelding sendt!");
      fetchMyBookings().then((b) => setExistingBooking(b.find((b) => b.post_id === post.id) || null));
    } catch (err) {
      toast.error(err.message || "Påmelding feilet. Prøv igjen.");
    }
  };

  const handleUpdate = async () => {
    if (!existingBooking || !newLevel) return;
    try {
      await updateBooking(existingBooking.id, { tajweed_level: newLevel });
      toast.success("Oppdatert!");
      setExistingBooking({ ...existingBooking, tajweed_level: newLevel });
      setEditing(false);
    } catch (err) { toast.error(err.message); }
  };

  const handleCancel = async () => {
    if (!existingBooking) return;
    try {
      await cancelBooking(existingBooking.id);
      toast.info("Påmelding avbestilt");
      setExistingBooking(null);
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return (
    <div className="kd-page kd-center">
      <div className="spinner-border" style={{ color: "#d4af37" }} />
    </div>
  );

  if (!post) return (
    <div className="kd-page">
      <div className="kd-nav-strip">
        <div className="kd-container">
          <button className="kd-back-btn" onClick={() => navigate("/konsultasjoner")}>← Tilbake</button>
        </div>
      </div>
      <div className="kd-center flex-grow-1">
        <p className="kd-not-found">Konsultasjon ikke funnet.</p>
      </div>
      <Footer />
    </div>
  );

  const dateObj = post.date ? new Date(post.date + "T00:00:00") : null;
  const full = dateObj
    ? dateObj.toLocaleDateString("no-NO", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <div className="kd-page">

      {/* Nav strip */}
      <div className="kd-nav-strip">
        <div className="kd-container">
          <button className="kd-back-btn" onClick={() => navigate("/konsultasjoner")}>
            ← Alle konsultasjoner
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="kd-body">
        <div className="kd-container kd-narrow">

          {/* Header */}
          <div className="kd-header">
            <span className="kd-eyebrow">Al-Yasin · Koranskole</span>
            <h1 className="kd-title">{post.header}</h1>
            <div className="kd-meta">
              {full && <span className="kd-meta-item">📅 {full}</span>}
              {post.time && <span className="kd-meta-item">🕐 {post.time}</span>}
            </div>
            {post.description && (
              <p className="kd-desc" style={{ whiteSpace: "pre-wrap" }}>{post.description}</p>
            )}
          </div>

          <div className="kd-divider" />

          {/* Form section */}
          <div className="kd-form-section">

            {existingBooking && !editing ? (
              <>
                <div className="kd-section-title">Din påmelding</div>

                <div className="kd-booked-banner">
                  <span className="kd-booked-icon">✓</span>
                  <div>
                    <strong>Påmeldt</strong>
                    <p>Registrert som <strong>{existingBooking.student_name}</strong></p>
                  </div>
                </div>

                <div className="kd-info-grid">
                  <div className="kd-info-item"><label>Navn</label><span>{existingBooking.student_name}</span></div>
                  <div className="kd-info-item"><label>E-post</label><span>{existingBooking.student_email}</span></div>
                  <div className="kd-info-item"><label>Kjønn</label><span>{existingBooking.sex || "–"}</span></div>
                  <div className="kd-info-item"><label>Alder</label><span>{existingBooking.age_range || "–"}</span></div>
                  <div className="kd-info-item"><label>Telefon</label><span>{existingBooking.phone || "–"}</span></div>
                  <div className="kd-info-item"><label>Tajweed-nivå</label><span>{existingBooking.tajweed_level || "–"}</span></div>
                  <div className="kd-info-item"><label>Påmeldt</label><span>{new Date(existingBooking.created_at).toLocaleDateString("no-NO")}</span></div>
                </div>

                <div className="kd-actions">
                  <button className="kd-btn kd-btn-outline-blue"
                    onClick={() => { setNewLevel(existingBooking.tajweed_level || ""); setEditing(true); }}>
                    Endre nivå
                  </button>
                  <button className="kd-btn kd-btn-outline-red" onClick={handleCancel}>
                    Avbestill
                  </button>
                </div>
              </>

            ) : existingBooking && editing ? (
              <>
                <div className="kd-section-title">Endre tajweed-nivå</div>
                <div className="kd-field">
                  <label>Nytt nivå</label>
                  <select className="kd-input" value={newLevel} onChange={(e) => setNewLevel(e.target.value)}>
                    <option value="">Velg tajweed-nivå</option>
                    <option value="Nybegynner">Nybegynner</option>
                    <option value="Middels">Middels</option>
                    <option value="Avansert">Avansert</option>
                  </select>
                </div>
                <div className="kd-actions">
                  <button className="kd-btn kd-btn-primary" onClick={handleUpdate} disabled={!newLevel}>Lagre endring</button>
                  <button className="kd-btn kd-btn-ghost" onClick={() => setEditing(false)}>Avbryt</button>
                </div>
              </>

            ) : (
              <form onSubmit={handleBooking}>
                <div className="kd-section-title">Meld deg på</div>

                {isMember ? (
                  <div className="kd-notice kd-notice-green">
                    Logget inn som <strong>{name || user}</strong> — informasjonen din er forhåndsutfylt.
                  </div>
                ) : (
                  <div className="kd-notice kd-notice-blue">
                    Ikke medlem? <a href="/signup">Registrer deg</a> for automatisk utfylling neste gang.
                  </div>
                )}

                <div className="kd-field">
                  <label htmlFor="kd-name">Navn</label>
                  <input id="kd-name" name="studentName" className="kd-input" placeholder="Ditt fulle navn"
                    defaultValue={name || user || ""} readOnly={isMember} autoComplete="name" required />
                </div>

                <div className="kd-field">
                  <label htmlFor="kd-email">E-post</label>
                  <input id="kd-email" type="email" name="studentEmail" className="kd-input" placeholder="din@epost.no"
                    defaultValue={email || ""} readOnly={isMember} autoComplete="email" required />
                </div>

                <div className="kd-field">
                  <label htmlFor="kd-phone">Telefon <span style={{ fontWeight: 400, textTransform: "none", color: "#aaa" }}>(valgfritt)</span></label>
                  <input id="kd-phone" name="phone" type="tel" className="kd-input" placeholder="12345678"
                    autoComplete="tel" inputMode="numeric" maxLength={15}
                    onChange={(e) => { e.target.value = e.target.value.replace(/\D/g, ""); }} />
                </div>

                <div className="kd-row">
                  <div className="kd-field">
                    <label htmlFor="kd-sex">Kjønn</label>
                    <select id="kd-sex" name="sex" className="kd-input" defaultValue={sex || ""} disabled={isMember} required>
                      <option value="">Velg</option>
                      <option value="Mann">Mann</option>
                      <option value="Kvinne">Kvinne</option>
                    </select>
                    {isMember && <input type="hidden" name="sex" value={sex || ""} />}
                  </div>
                  <div className="kd-field">
                    <label htmlFor="kd-age">Aldersgruppe</label>
                    <select id="kd-age" name="age" className="kd-input" defaultValue={age || ""} disabled={isMember} required>
                      <option value="">Velg</option>
                      <option value="7-12">7–12</option>
                      <option value="13-18">13–18</option>
                      <option value="19-30">19–30</option>
                      <option value="30+">30+</option>
                    </select>
                    {isMember && <input type="hidden" name="age" value={age || ""} />}
                  </div>
                </div>

                <div className="kd-field">
                  <label htmlFor="kd-level">Tajweed-nivå</label>
                  <select id="kd-level" name="level" className="kd-input" required>
                    <option value="">Velg nivå</option>
                    <option value="Nybegynner">Nybegynner</option>
                    <option value="Middels">Middels</option>
                    <option value="Avansert">Avansert</option>
                  </select>
                </div>

                <button type="submit" className="kd-btn kd-btn-primary kd-btn-full">
                  {isMember ? "Bekreft påmelding som medlem" : "Bekreft påmelding"}
                </button>
              </form>
            )}

          </div>
        </div>
      </div>

      <Footer />
      <ToastContainer position="top-center" autoClose={3500} hideProgressBar={false} theme="colored" />
    </div>
  );
}

export default KonsultasjonDetalj;
